import { BaseClickable, palette, Stack, style, Text } from '@solvhealth/jigsaw-v1';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ChevronRight } from '@material-ui/icons';
import history from '~/core/history';
import { ProviderWithCareTypes } from '~/core/dapi/careTeam';
import { getImageObject, getProviderName } from '~/components/ProviderGroup/Provider/util';
import { createProviderUrlFromProvider } from '~/core/url/provider';
import { analyticsTrackEvent } from '~/core/analytics';
import { ACCOUNT_HOME_CLICK_BOOK_AGAIN_SUGGESTION } from '~/core/analytics/events';

const ProviderImage = styled.img.withConfig({ componentId: 'sc-rmg22v-0' })(
  style({
    borderRadius: 'full',
    overflow: 'hidden',
    objectFit: 'fill',
    flexShrink: 0,
  })
);

type Props = {
  provider: ProviderWithCareTypes;
  className?: string;
};

/**
 * Card to be displayed on the account home that displays a given provider
 * as a rebooking option and the time it was most recently booked.
 */
const ProviderBookAgainCard = ({ provider, className }: Props) => {
  const { t } = useTranslation(['account']);

  const image = getImageObject(provider);

  const handleClick = () => {
    const url = createProviderUrlFromProvider(provider);
    history.push(url, { from: 'CARE_TEAM' });
    analyticsTrackEvent(ACCOUNT_HOME_CLICK_BOOK_AGAIN_SUGGESTION, {
      provider: provider.id,
      last_booked: provider.last_booked,
    });
  };

  return (
    <BaseClickable
      className={className}
      contentProps={{ width: '100%', alignSelf: 'stretch' }}
      flexShrink={0}
      onClick={handleClick}
    >
      <Stack
        alignSelf="stretch"
        border="solid 1px"
        borderColor="gray-200"
        borderRadius="base"
        boxShadow="xs"
        overflow="hidden"
        width="100%"
      >
        <Stack flex="1" p={2} space={2}>
          <Stack flex="1" justifyContent="space-between" row space={1}>
            <Stack alignSelf="stretch" space={2}>
              <Stack flex="1">
                <Text variant="subtitle">{getProviderName(provider)}</Text>
                {provider.last_booked && (
                  <Text muted>
                    {t('account:availability.lastBooked', {
                      date: provider.last_booked,
                    })}
                  </Text>
                )}
              </Stack>
            </Stack>
            <ProviderImage height={44} src={image.fullRes} width={44} />
          </Stack>
          <Stack alignItems="center" justifyContent="flex-start" pb={5} row space={0.5}>
            <Text textColor="crunchberry">{t('account:availability.bookAppointment')}</Text>
            <ChevronRight htmlColor={palette.crunchberry.base} style={{ fontSize: 18 }} />
          </Stack>
        </Stack>
      </Stack>
    </BaseClickable>
  );
};

export default ProviderBookAgainCard;
